/**
 * Provider component that exposes focusNavigation and controllerInput deps from container.
 * This replaces the old React Context-based providers with the new dependency management system.
 */
import { createContext, useContext, ReactNode } from 'react'
import type { FocusNavigation } from '../../../deps/mode/focusNavigation/focusNavigation.types'
import type { ControllerInput } from '@merels/input/controllerInput'

const FocusNavigationDepContext = createContext<FocusNavigation | null>(null)
const ControllerInputDepContext = createContext<ControllerInput | null>(null)

export interface FocusNavigationProviderProps {
  focusNavigation: FocusNavigation
  controllerInput: ControllerInput
  children: ReactNode
}

export function FocusNavigationProvider({
  focusNavigation,
  controllerInput,
  children,
}: FocusNavigationProviderProps) {
  return (
    <FocusNavigationDepContext.Provider value={focusNavigation}>
      <ControllerInputDepContext.Provider value={controllerInput}>
        {children}
      </ControllerInputDepContext.Provider>
    </FocusNavigationDepContext.Provider>
  )
}

export function useFocusNavigationDep(): FocusNavigation {
  const context = useContext(FocusNavigationDepContext)
  if (!context) {
    throw new Error('useFocusNavigationDep must be used within FocusNavigationProvider')
  }
  return context
}

export function useControllerInputDep(): ControllerInput {
  const context = useContext(ControllerInputDepContext)
  if (!context) {
    throw new Error('useControllerInputDep must be used within FocusNavigationProvider')
  }
  return context
}
